/**
 * WHAT: Barrel export for all custom React hooks in the DAMA application.
 * WHY:  Provides a single import point so components can pull in any hook via
 *       `import { useSafeAsync, useFilters } from '../hooks'` instead of
 *       reaching into individual files. This keeps imports consistent and
 *       makes it easy to discover available hooks.
 *
 * Memory Leak Prevention:
 * - useSafeTimeout: Safe setTimeout with auto-cleanup
 * - useSafeInterval: Safe setInterval with auto-cleanup
 * - useSafeAsync: Safe async operations with abort support
 * - useSafeState: useState that only updates when mounted
 * - useMountedRef: Track component mount status
 *
 * Event Handling:
 * - useEventListener: Safe event listeners with auto-cleanup
 * - useWindowEvent: Window event listener shorthand
 * - useDocumentEvent: Document event listener shorthand
 * - useKeyPress: Keyboard shortcut handling
 * - useClickOutside: Detect clicks outside an element
 *
 * Data Management:
 * - usePagination: Table/list pagination with navigation
 * - useFilters: Filter state management with apply functions
 * - useSearchFilter: Simplified text search filtering
 *
 * Utility:
 * - useOnlineStatus: Track browser online/offline status
 * - useVisibilityChange: Track page visibility changes
 * - useAutoTimeout: Auto-trigger timeout on value change
 * - useConditionalInterval: Interval that can be paused
 * - useAsyncCallback: Safe async event handlers
 */

// Safe timeout hooks
export {
  useSafeTimeout,
  useAutoTimeout
} from './useSafeTimeout';

// Safe interval hooks
export {
  useSafeInterval,
  useConditionalInterval
} from './useSafeInterval';

// Safe async hooks
export {
  useMountedRef,
  useSafeState,
  useSafeAsync,
  useAsyncCallback
} from './useSafeAsync';

// Event listener hooks
export {
  useEventListener,
  useWindowEvent,
  useDocumentEvent,
  useKeyPress,
  useClickOutside,
  useOnlineStatus,
  useVisibilityChange
} from './useEventListener';

// Data management hooks
export {
  usePagination,
  usePaginationWithReset
} from './usePagination';

export {
  useFilters,
  useSearchFilter
} from './useFilters';

// Default export for convenience
export { useSafeAsync as default } from './useSafeAsync';
